import { useState, useEffect, type ReactNode } from 'react'
import { useFeatureFlagContext } from '../contexts/FeatureFlagContext'
import { siteProtectionApiService } from '../services/siteProtectionApiService'
import { dismissSplash } from '../splash'
import { colors, fontSize, radius, shadow, spacing } from '../styles/theme'

const STORAGE_KEY = 'docm_site_token'

export default function SitePasswordGate({ children }: { children: ReactNode }) {
  const { flags, isLoading: flagsLoading } = useFeatureFlagContext()
  const [state, setState] = useState<'loading' | 'open' | 'locked'>('loading')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (flagsLoading) return
    if (!flags['SitePasswordProtection']) { setState('open'); return }

    const token = localStorage.getItem(STORAGE_KEY)
    siteProtectionApiService.check(token)
      .then(res => setState(res.granted ? 'open' : 'locked'))
      .catch(() => setState('locked'))
  }, [flagsLoading, flags])

  // This gate renders `null` until the feature flags and the site-password check have both
  // returned, so it is the first thing in the tree with anything to show. The splash stays up
  // until then — dismissing it earlier (as index.tsx used to, at module-eval time) leaves a blank
  // page for the length of those requests, which is exactly the lag it exists to cover.
  const ready = !flagsLoading && state !== 'loading'
  useEffect(() => {
    if (ready) dismissSplash()
  }, [ready])

  if (state === 'loading' || flagsLoading) return null
  if (state === 'open') return <>{children}</>

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      const res = await siteProtectionApiService.verify(password)
      if (res.granted && res.token) {
        localStorage.setItem(STORAGE_KEY, res.token)
        setState('open')
      }
    } catch {
      setError('Incorrect password.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: colors.background,
    }}>
      <div style={{
        width: '100%', maxWidth: 360,
        padding: spacing.xxl,
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: radius.lg,
        boxShadow: shadow.lg,
      }}>
        <div style={{ marginBottom: spacing.xl, textAlign: 'center' }}>
          <div style={{ fontWeight: 800, fontSize: fontSize.xxl, color: colors.primary, letterSpacing: '-0.5px', marginBottom: spacing.xs }}>
            DocMorphia
          </div>
          <div style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>
            This environment is password protected.
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Enter password"
            autoFocus
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: `${spacing.sm} ${spacing.sm}`,
              fontSize: fontSize.base,
              border: `1px solid ${error ? colors.error : colors.border}`,
              borderRadius: radius.md,
              background: colors.background,
              color: colors.text,
              outline: 'none',
              marginBottom: spacing.sm,
            }}
          />
          {error && (
            <div style={{ color: colors.error, fontSize: fontSize.sm, marginBottom: spacing.sm }}>
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting || !password}
            style={{
              width: '100%',
              padding: `${spacing.sm} ${spacing.md}`,
              fontSize: fontSize.base,
              fontWeight: 600,
              background: submitting || !password ? colors.border : colors.primary,
              color: '#fff',
              border: 'none',
              borderRadius: radius.md,
              cursor: submitting || !password ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? 'Checking…' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  )
}
